import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Star, Phone, Mail, MapPin, Euro, Flame, Download, Loader2, Globe } from "lucide-react";
import { downloadCsv } from "@/lib/csv";

const STAGES = [
  { key: "nouveaux", label: "Nouveaux", accent: "border-slate-700 text-slate-300", dot: "bg-slate-500" },
  { key: "contactes", label: "Contactés", accent: "border-cyan-500/40 text-cyan-300", dot: "bg-cyan-500" },
  { key: "rdv", label: "RDV planifié", accent: "border-amber-500/40 text-amber-300", dot: "bg-amber-500" },
  { key: "gagnes", label: "Gagnés", accent: "border-emerald-500/40 text-emerald-300", dot: "bg-emerald-500" },
  { key: "perdus", label: "Perdus", accent: "border-rose-500/40 text-rose-300", dot: "bg-rose-500" },
];

export default function Prospects() {
  const nav = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichStats, setEnrichStats] = useState(null);

  const enrichEmails = async () => {
    setEnriching(true);
    try {
      const r = await api.post("/prospects/enrich-emails", { limit: 20 });
      setEnrichStats(r.data);
      if (r.data.processed === 0) toast.info("Tous les prospects sans email ont déjà été analysés");
      else toast.success(`${r.data.emails_found} email(s), ${r.data.websites_found} site(s), ${r.data.phones_found} téléphone(s) trouvés sur ${r.data.processed} prospects`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enrichissement");
    } finally {
      setEnriching(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadCsv("/prospects/export.csv");
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur export CSV");
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    const params = search ? { search } : {};
    api.get("/prospects", { params }).then((r) => setProspects(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const changeStage = async (id, stage) => {
    // optimistic
    setProspects((prev) => prev.map((p) => p.prospect_id === id ? { ...p, stage } : p));
    try {
      await api.patch(`/prospects/${id}`, { stage });
      toast.success("Étape mise à jour");
    } catch {
      toast.error("Erreur");
      load();
    }
  };

  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("prospect_id");
    if (id) changeStage(id, stage);
    setDragging(null);
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-2">// Pipeline</div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-100 tracking-tight">Pipeline CRM</h1>
          <p className="text-slate-400 mt-1.5 text-sm">Glissez les prospects entre les colonnes pour changer leur étape.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="prospects-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher entreprise, contact, ville…"
              className="pl-9 pr-4 py-2.5 w-80 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none text-sm"
            />
          </div>
          <button
            data-testid="enrich-emails-button"
            onClick={enrichEmails}
            disabled={enriching}
            title="Recherche les sites web des entreprises et y extrait les emails / téléphones publics (20 prospects par passage)"
            className="px-4 py-2.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60"
          >
            {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} {enriching ? "Analyse des sites…" : "Trouver des emails"}
          </button>
          <button
            data-testid="export-csv-button"
            onClick={exportCsv}
            disabled={exporting}
            className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exporter CSV
          </button>
        </div>
      </header>

      {enrichStats && (
        <div data-testid="enrich-stats" className="mb-5 p-4 bg-[#121215] border border-cyan-500/30 rounded-xl text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-slate-200">
              <span className="font-semibold text-cyan-300">{enrichStats.emails_found}</span> email(s), <span className="font-semibold text-cyan-300">{enrichStats.websites_found}</span> site(s) et <span className="font-semibold text-cyan-300">{enrichStats.phones_found}</span> téléphone(s) trouvés sur {enrichStats.processed} prospects analysés
              {enrichStats.remaining > 0 && <span className="text-slate-500"> · {enrichStats.remaining} restant(s) à analyser — relancez « Trouver des emails »</span>}
            </div>
            <button data-testid="enrich-stats-close" onClick={() => setEnrichStats(null)} className="text-xs text-slate-500 hover:text-slate-300">Fermer</button>
          </div>
          {enrichStats.details.length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {enrichStats.details.map((d) => (
                <button key={d.prospect_id} onClick={() => nav(`/prospects/${d.prospect_id}`)} className="text-left px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 text-xs">
                  <span className="text-slate-200 font-medium">{d.company}</span>
                  {d.email && <span className="text-emerald-300"> · {d.email}</span>}
                  {d.phone && <span className="text-slate-400"> · {d.phone}</span>}
                  {!d.email && d.website && <span className="text-cyan-400"> · site trouvé</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}


      <div className="flex gap-4 overflow-x-auto pb-6 pt-2">
        {STAGES.map((stage) => {
          const items = prospects.filter((p) => p.stage === stage.key);
          const total = items.reduce((s, p) => s + (Number(p.estimated_value) || 0), 0);
          return (
            <div
              key={stage.key}
              data-testid={`kanban-column-${stage.key}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, stage.key)}
              className={`w-80 shrink-0 bg-[#0d0d12] rounded-xl border border-slate-800/80 p-3 flex flex-col ${dragging && dragging !== stage.key ? "border-cyan-500/40" : ""}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
                  <span className={`font-heading font-semibold text-sm ${stage.accent.split(" ")[1]}`}>{stage.label}</span>
                  <span className="text-xs font-mono text-slate-500">·</span>
                  <span data-testid={`kanban-count-${stage.key}`} className="text-xs font-mono text-slate-500">{items.length}</span>
                </div>
                <span className="text-xs font-mono text-slate-500">{total.toLocaleString("fr-FR")}€</span>
              </div>

              <div className="flex-1 space-y-2 min-h-[300px]">
                {items.map((p) => (
                  <div
                    key={p.prospect_id}
                    data-testid={`prospect-card-${p.prospect_id}`}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("prospect_id", p.prospect_id); setDragging(stage.key); }}
                    onDragEnd={() => setDragging(null)}
                    onClick={() => nav(`/prospects/${p.prospect_id}`)}
                    className={`kanban-card p-3.5 bg-[#121215] border rounded-lg hover:border-cyan-500/40 ${p.email_opens > 0 ? "border-amber-500/40" : "border-slate-800"}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-medium text-slate-100 text-sm leading-tight truncate flex-1">{p.company}</div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.email_opens > 0 && (
                          <span data-testid={`hot-badge-${p.prospect_id}`} className="flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" title={`${p.email_opens} ouverture(s) d'email`}>
                            <Flame className="w-3 h-3" />{p.email_opens}
                          </span>
                        )}
                        {p.rating > 0 && (
                          <div className="flex items-center gap-0.5 text-xs text-amber-400">
                            <Star className="w-3 h-3 fill-amber-400" />
                            <span className="font-mono">{p.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {p.contact_name && <div className="text-xs text-slate-400 mb-2">{p.contact_name}</div>}
                    <div className="flex flex-wrap gap-1.5 text-xs text-slate-500">
                      {p.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>}
                      {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone.slice(0,11)}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-800">
                      {p.estimated_value > 0 ? (
                        <span className="text-xs font-mono text-emerald-400 flex items-center gap-1"><Euro className="w-3 h-3" />{Number(p.estimated_value).toLocaleString("fr-FR")}</span>
                      ) : <span />}
                      {p.email && <Mail className="w-3 h-3 text-slate-500" />}
                    </div>
                    {(p.tags || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.tags.slice(0, 2).map((t, i) => (
                          <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-center text-xs font-mono text-slate-600 py-8">Aucun prospect</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
